import {
  ComputerDesktopIcon,
  ShareIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  VideoCameraIcon,
  VideoCameraSlashIcon,
} from '@heroicons/react/20/solid';
import {useState, useCallback, useEffect, useRef} from 'react';
import * as U from './util';
import {AudioMeter} from './audio-meter';
import * as aircoreMedia from '@aircore/aircore-media';
import {v4 as uuid4} from 'uuid';
import {
  emitEvent,
  useEventHandler,
  Event,
  RemoteStreamMuteStateChange,
  RemoteStreamVadStateChange,
  RemoteStreamConnectionStateChange,
} from './events';
import {
  BUTTON_STYLE_STREAM_PANEL,
  BUTTON_STYLE_STREAM_PANEL_LEFT,
  BUTTON_STYLE_STREAM_PANEL_RIGHT,
  PANEL_STYLE_STREAM,
  VIDEO_STYLE,
} from './styles';
import {VolumeSlider} from './volume-slider';

interface RemoteStreamPanelProps {
  stream: aircoreMedia.RemoteStream;
}

const VIDEO_STYLES = ['object-contain', 'invert', 'grayscale', 'sepia', 'hue-rotate-90', 'blur'];

export function RemoteStreamPanel({stream}: RemoteStreamPanelProps) {
  const [isAudioMutedLocally, setIsAudioMutedLocally] = useState(stream.localAudioMuted);
  const [isAudioMutedRemotely, setIsAudioMutedRemotely] = useState(stream.remoteAudioMuted);
  const [isVideoMutedRemotely, setIsVideoMutedRemotely] = useState(stream.remoteVideoMuted);
  const [isVoiceDetected, setIsVoiceDetected] = useState(stream.voiceActivity);
  const [connectionState, setConnectionState] = useState(stream.connectionState);
  const [multiplyVideo, setMultiplyVideo] = useState(false);
  const [elementId] = useState(uuid4());
  const [videoWidth, setVideoWidth] = useState<number | undefined>(undefined);
  const [videoHeight, setVideoHeight] = useState<number | undefined>(undefined);
  const [pendingWidth, setPendingWidth] = useState(256);
  const [pendingHeight, setPendingHeight] = useState(256);
  const audioLevelGetter = () => stream.audioLevel;
  const initialSizeSetRef = useRef(false);

  useEventHandler(
    Event.BIGSUR_REMOTE_STREAM_LOCAL_AUDIO_MUTE_CHANGED,
    (change: RemoteStreamMuteStateChange) => {
      if (change.stream === stream) {
        setIsAudioMutedLocally(change.state.muted);
      }
    },
    [stream]
  );
  useEventHandler(
    Event.BIGSUR_REMOTE_STREAM_REMOTE_AUDIO_MUTE_CHANGED,
    (change: RemoteStreamMuteStateChange) => {
      if (change.stream === stream) {
        setIsAudioMutedRemotely(change.state.muted);
      }
    },
    [stream]
  );
  useEventHandler(
    Event.BIGSUR_REMOTE_STREAM_REMOTE_VIDEO_MUTE_CHANGED,
    (change: RemoteStreamMuteStateChange) => {
      if (change.stream === stream) {
        setIsVideoMutedRemotely(change.state.muted);
      }
    },
    [stream]
  );
  useEventHandler(
    Event.BIGSUR_REMOTE_STREAM_VAD_CHANGED,
    (change: RemoteStreamVadStateChange) => {
      U.debug(`Remote stream VAD changed: ${change.state.voiceActivity}`);
      if (change.stream === stream) {
        setIsVoiceDetected(change.state.voiceActivity);
      }
    },
    [stream]
  );
  useEventHandler(
    Event.BIGSUR_REMOTE_STREAM_CONNECTION_CHANGED,
    (change: RemoteStreamConnectionStateChange) => {
      if (change.stream === stream) {
        U.appLogO('Remote stream connection state changed: ', change.state);
        setConnectionState(change.state.newConnectionState);
      }
    },
    [stream]
  );
  const handleMuteAudio = useCallback(() => {
    emitEvent(Event.USER_MUTE_LOCAL_AUDIO, stream);
  }, [stream]);

  const handleUnmuteAudio = useCallback(() => {
    emitEvent(Event.USER_UNMUTE_LOCAL_AUDIO, stream);
  }, [stream]);

  const handleMuteVideo = useCallback(() => {
    emitEvent(Event.USER_MUTE_LOCAL_VIDEO, stream);
  }, [stream]);

  const handleUnmuteVideo = useCallback(() => {
    emitEvent(Event.USER_UNMUTE_LOCAL_VIDEO, stream);
  }, [stream]);

  const handleMultipleVideoChange = useCallback(() => {
    setMultiplyVideo(!multiplyVideo);
  }, [multiplyVideo]);

  const handleLogStats = useCallback(() => {
    emitEvent(Event.USER_LOG_STREAM_STATS, stream);
  }, [stream]);

  const handleChangeVolume = useCallback(
    (volume: number) => {
      // Only execute the change if the volume is valid
      if (!isNaN(volume)) {
        stream.outputVolume = volume;
      }
    },
    [stream]
  );

  const handleApplySize = useCallback(() => {
    setVideoWidth(pendingWidth);
    setVideoHeight(pendingHeight);
  }, [pendingWidth, pendingHeight]);

  const handleVideoMetadataLoaded = useCallback((naturalWidth: number, naturalHeight: number) => {
    if (initialSizeSetRef.current) return;
    initialSizeSetRef.current = true;

    // Lock to natural dimensions to prevent feedback loop
    setVideoWidth(naturalWidth);
    setVideoHeight(naturalHeight);
    setPendingWidth(naturalWidth);
    setPendingHeight(naturalHeight);
  }, []);

  const outline = isVoiceDetected ? 'outline outline-2 outline-green-500' : null;
  let panelContent = (
    <>
      <div className="w-full sm:w-48 h-12 sm:h-auto bg-white flex">
        {isAudioMutedLocally && <SpeakerXMarkIcon className="h-full text-red-500" />}
        <AudioMeter levelReader={audioLevelGetter} />
      </div>
      <div className="w-full flex flex-col sm:rounded-r-md justify-center items-center bg-gray-300 p-5">
        <div className="w-full flex sm:flex-row justify-center items-center sm:justify-normal sm:items-normal">
          <VolumeSlider initialVolume={stream.outputVolume} setVolume={handleChangeVolume} />
        </div>
        <div className="w-full flex flex-row sm:rounded-r-md items-center bg-gray-300">
          <ShareIcon className="h-12 sm:h-24 text-gray-600 py-2" />
          {isAudioMutedRemotely ? (
            <SpeakerXMarkIcon className="h-12 sm:h-24 text-red-500 py-2" />
          ) : (
            <SpeakerWaveIcon className="h-12 sm:h-24 text-gray-600 py-2" />
          )}
          {isVideoMutedRemotely ? (
            <VideoCameraSlashIcon className="h-12 sm:h-24 text-red-500 py-2" />
          ) : (
            <VideoCameraIcon className="h-12 sm:h-24 text-gray-600 py-2" />
          )}
          {stream.isScreenShare && <ComputerDesktopIcon className="h-12 sm:h-24 text-gray-600 py-2" />}
          <p className=" p-1 w-full text-gray-600 text-sm bold">
            userId: <b>{stream.userId}</b> <br />
            connection: <b>{connectionState}</b> <br />
            metadata: <b>{stream.metadata?.dataValue}</b>
          </p>
        </div>
      </div>
      <RemoteStreamVideo
        stream={stream}
        style=""
        id={elementId}
        isMutedRemotely={isVideoMutedRemotely}
        width={videoWidth}
        height={videoHeight}
        onMetadataLoaded={handleVideoMetadataLoaded}
      />
    </>
  );

  if (multiplyVideo) {
    panelContent = (
      <>
        <div className="w-full h-full bg-white flex flex-wrap gap-2 p-2">
          {VIDEO_STYLES.map((style, index) => {
            const id = `${elementId}-${index}`;
            return (
              <TitledVideoTile key={id} id={id} style={style} stream={stream} isMutedRemotely={isVideoMutedRemotely} />
            );
          })}
        </div>
      </>
    );
  }

  return (
    <div className={`${PANEL_STYLE_STREAM} ${outline}`}>
      <div className="w-auto h-auto rounded-t-md sm:rounded-l-md sm:rounded-tr-none p-2 flex flex-col justify-around gap-1">
        <p className="text-center text-sm text-white bold">Audio</p>
        <span>
          <button className={BUTTON_STYLE_STREAM_PANEL_LEFT} onClick={handleMuteAudio} hidden={stream.isScreenShare}>
            Mute
          </button>
          <button className={BUTTON_STYLE_STREAM_PANEL_RIGHT} onClick={handleUnmuteAudio} hidden={stream.isScreenShare}>
            Unmute
          </button>
        </span>
        <p className="text-center text-sm text-white bold">Video</p>
        <span>
          <button className={BUTTON_STYLE_STREAM_PANEL_LEFT} onClick={handleMuteVideo} hidden={stream.isScreenShare}>
            Mute
          </button>
          <button className={BUTTON_STYLE_STREAM_PANEL_RIGHT} onClick={handleUnmuteVideo} hidden={stream.isScreenShare}>
            Unmute
          </button>
        </span>
        <button className={BUTTON_STYLE_STREAM_PANEL} onClick={handleMultipleVideoChange}>
          {multiplyVideo ? 'UniVid' : 'MultiVid'}
        </button>
        <p className="text-center text-sm text-white bold">Size</p>
        <div className="flex flex-col gap-1 px-1">
          <label className="text-xs text-white">Width</label>
          <input
            type="number"
            min="32"
            max="1920"
            value={pendingWidth}
            onChange={e => setPendingWidth(Number(e.target.value))}
            className="w-full px-2 py-1 text-sm rounded border border-gray-300"
          />
          <label className="text-xs text-white">Height</label>
          <input
            type="number"
            min="32"
            max="1080"
            value={pendingHeight}
            onChange={e => setPendingHeight(Number(e.target.value))}
            className="w-full px-2 py-1 text-sm rounded border border-gray-300"
          />
          <button className={`${BUTTON_STYLE_STREAM_PANEL} mt-1`} onClick={handleApplySize}>
            Apply
          </button>
        </div>
        <button className={BUTTON_STYLE_STREAM_PANEL} onClick={handleLogStats}>
          Log Stats
        </button>
      </div>
      {panelContent}
    </div>
  );
}

interface RemoteStreamVideoElementProps {
  stream: aircoreMedia.RemoteStream;
  id: string;
  style: string;
  isMutedRemotely: boolean;
  width?: number;
  height?: number;
  onMetadataLoaded?: (naturalWidth: number, naturalHeight: number) => void;
}

function RemoteStreamVideo({
  stream,
  id,
  style,
  isMutedRemotely,
  width,
  height,
  onMetadataLoaded,
}: RemoteStreamVideoElementProps) {
  const [isEnabled, setIsEnabled] = useState(true);
  const isAttachedRef = useRef(false);
  const rafIdRef = useRef<number | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const shouldRenderVideo = isEnabled && !isMutedRemotely;

    console.log('[RemoteStreamVideo] Effect triggered', {
      id,
      isEnabled,
      isMutedRemotely,
      shouldRenderVideo,
      isAttached: isAttachedRef.current,
    });

    if (shouldRenderVideo && !isAttachedRef.current) {
      console.log('[RemoteStreamVideo] Scheduling addRenderTarget via RAF', id);
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        const videoElement = document.getElementById(id);
        console.log('[RemoteStreamVideo] RAF callback - videoElement:', videoElement);
        if (videoElement) {
          console.log('[RemoteStreamVideo] Calling addRenderTarget', id);
          void stream.addRenderTarget(id);
          isAttachedRef.current = true;
          console.log('[RemoteStreamVideo] addRenderTarget completed', id);
        } else {
          console.warn('[RemoteStreamVideo] Video element not found in DOM', id);
        }
      });
    } else if (!shouldRenderVideo && isAttachedRef.current) {
      console.log('[RemoteStreamVideo] Calling removeRenderTarget', id);
      stream.removeRenderTarget(id);
      isAttachedRef.current = false;
    }

    return () => {
      if (rafIdRef.current !== null) {
        console.log('[RemoteStreamVideo] Cleanup: canceling RAF', id);
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (isAttachedRef.current) {
        console.log('[RemoteStreamVideo] Cleanup: removeRenderTarget', id);
        stream.removeRenderTarget(id);
        isAttachedRef.current = false;
      }
    };
  }, [stream, id, isEnabled, isMutedRemotely]);

  const handleVideoToggle = useCallback(() => {
    setIsEnabled(prev => !prev);
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current && onMetadataLoaded) {
      const {videoWidth, videoHeight} = videoRef.current;
      if (videoWidth > 0 && videoHeight > 0) {
        onMetadataLoaded(videoWidth, videoHeight);
      }
    }
  }, [onMetadataLoaded]);

  const videoStyle: React.CSSProperties = {
    width: width ? `${width}px` : undefined,
    height: height ? `${height}px` : undefined,
    objectFit: 'fill',
  };

  return (
    <video
      ref={videoRef}
      playsInline
      autoPlay
      className={`${style} ${VIDEO_STYLE}`}
      style={videoStyle}
      id={id}
      onClick={handleVideoToggle}
      onLoadedMetadata={handleLoadedMetadata}
    />
  );
}

function TitledVideoTile({stream, id, style, isMutedRemotely}: RemoteStreamVideoElementProps) {
  const [isEnlarged, setIsEnlarged] = useState(false);

  const handleEnlargedToggle = useCallback(() => {
    setIsEnlarged(prev => !prev);
  }, []);

  return (
    <div
      className={`p-1 border-2 border-gray-500 ${isEnlarged ? 'h-auto w-96' : 'h-44 w-36'} rounded-md bg-gray-400 flex flex-col justify-evenly`}
      key={id}
    >
      <p
        className="mb-1 text-black text-xs text-center border border-black rounded-md p-1 bg-blue-200 cursor-pointer"
        onClick={handleEnlargedToggle}
      >
        {style}
      </p>
      <RemoteStreamVideo
        stream={stream}
        style={`h-full w-full ${isEnlarged ? '' : 'max-h-32'} ${style}`}
        id={id}
        isMutedRemotely={isMutedRemotely}
      />
    </div>
  );
}
